import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/auth'
import { caregiverShifts, checkIn, checkOut, toggleTask, saveShiftNote } from '../../lib/api'

// `shift_date` is a Postgres date column, so it can arrive bare or as a full
// timestamp — slicing keeps the day from shifting across timezones.
const fmtD = (iso) => {
  if (!iso) return ''
  try {
    return new Date(String(iso).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-CA', {
      weekday: 'short', month: 'short', day: 'numeric',
    })
  } catch {
    return iso
  }
}
const fmtT = (iso) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })
  } catch {
    return iso
  }
}
const fmtRange = (a, b) => [a, b].filter(Boolean).join(' – ')

const textareaStyle = {
  fontFamily: 'Inter, Arial, sans-serif', fontSize: 16, color: '#191917', background: '#FFFFFF',
  border: '1px solid #D0C9B7', borderRadius: 4, padding: '12px 14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
}

export default function CaregiverPortal() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()

  const [shifts, setShifts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [drafts, setDrafts] = useState({})

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { shifts: rows } = await caregiverShifts()
        if (!cancelled) setShifts(rows)
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const patchShift = (id, patch) => setShifts((list) => list.map((s) => (s.id === id ? { ...s, ...patch } : s)))

  const guard = async (fn) => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const doCheckIn = (id) =>
    guard(async () => {
      const { checkedInAt } = await checkIn(id)
      patchShift(id, { checked_in_at: checkedInAt })
    })

  const doCheckOut = (id) =>
    guard(async () => {
      const { checkedOutAt } = await checkOut(id)
      patchShift(id, { checked_out_at: checkedOutAt })
    })

  const doToggle = (shiftId, task) =>
    guard(async () => {
      const { task: updated } = await toggleTask(task.id, !task.done)
      setShifts((list) =>
        list.map((s) =>
          s.id === shiftId ? { ...s, tasks: s.tasks.map((t) => (t.id === updated.id ? { ...t, done: updated.done } : t)) } : s
        )
      )
    })

  const doSaveNote = (id) =>
    guard(async () => {
      const text = (drafts[id] || '').trim()
      if (!text) return
      const { note } = await saveShiftNote(id, text)
      patchShift(id, { note })
      setDrafts((d) => ({ ...d, [id]: '' }))
    })

  const header = (
    <div style={{ background: '#003F3A', color: '#FAF7F0', padding: '14px 24px' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 21, fontWeight: 600 }}>Caregiver Portal</span>
        <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, fontSize: 15 }}>
          <span>Signed in as <strong>{user?.fullName}</strong></span>
          <button
            type="button"
            onClick={async () => {
              await signOut()
              navigate('/login')
            }}
            style={{ background: 'transparent', color: '#B7C4B7', border: '1px solid #527569', borderRadius: 4, padding: '8px 14px', fontWeight: 600, fontSize: 14, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer' }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F4EDDF', fontFamily: 'Inter, Arial, sans-serif' }}>
        {header}
        <div style={{ margin: '80px auto', textAlign: 'center', color: '#5D645B' }}>Loading your shifts…</div>
      </div>
    )
  }

  // Family notes come back per shift; the panel shows those of the most recent client.
  const familyNotes = shifts[0]?.family_notes || []

  return (
    <div style={{ minHeight: '100vh', background: '#F4EDDF', color: '#191917', fontFamily: 'Inter, Arial, sans-serif', fontSize: 17, lineHeight: 1.6 }}>
      {header}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 24px 64px 24px' }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 32, fontWeight: 600, color: '#003F3A', margin: '0 0 4px 0' }}>
          Your shifts
        </h1>
        <p style={{ color: '#5D645B', margin: '0 0 24px 0', fontSize: 16 }}>
          Check in, work through the task checklist, add your shift note, and check out.
        </p>

        {error && (
          <div style={{ background: '#FBEDEA', border: '1px solid #E0B4AA', color: '#8A2C1B', borderRadius: 8, padding: 16, marginBottom: 20 }} role="alert">
            {error}
          </div>
        )}

        {shifts.length === 0 && <p style={{ color: '#5D645B' }}>You have no shifts scheduled yet.</p>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {shifts.map((s) => {
            const inAt = s.checked_in_at ? fmtT(s.checked_in_at) : ''
            const badge = s.checked_out_at ? 'Completed' : s.checked_in_at ? 'In progress · in ' + inAt : 'Scheduled'
            const badgeBg = s.checked_out_at ? '#B7C4B7' : s.checked_in_at ? '#00564F' : '#EEE8DA'
            const badgeFg = s.checked_out_at ? '#003F3A' : s.checked_in_at ? '#FAF7F0' : '#5D645B'
            return (
              <div key={s.id} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: 'clamp(18px, 4vw, 30px)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                  <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 23, fontWeight: 600, color: '#003F3A' }}>
                      {fmtD(s.shift_date)} · {fmtRange(s.start_time, s.end_time)}
                    </div>
                    <div style={{ fontSize: 15, color: '#5D645B' }}>Client: {s.client_name}</div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', height: 'fit-content' }}>
                    {!s.checked_in_at && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => doCheckIn(s.id)}
                        style={{ background: '#00564F', color: '#FFFFFF', border: 'none', borderRadius: 4, padding: '12px 18px', minHeight: 48, fontWeight: 600, fontSize: 15, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer' }}
                      >
                        Check In
                      </button>
                    )}
                    {!!s.checked_in_at && !s.checked_out_at && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => doCheckOut(s.id)}
                        style={{ background: '#6B8B75', color: '#FFFFFF', border: 'none', borderRadius: 4, padding: '12px 18px', minHeight: 48, fontWeight: 600, fontSize: 15, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer' }}
                      >
                        Check Out
                      </button>
                    )}
                    <span style={{ background: badgeBg, color: badgeFg, borderRadius: 4, padding: '8px 12px', fontSize: 14, fontWeight: 600 }}>{badge}</span>
                  </div>
                </div>

                <div className="grid grid-2" style={{ gap: 8, marginBottom: 16 }}>
                  {(s.tasks || []).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      disabled={busy}
                      onClick={() => doToggle(s.id, t)}
                      style={{ textAlign: 'left', display: 'flex', gap: 10, alignItems: 'center', fontSize: 15, background: '#FFFFFF', border: '1px solid #EEE8DA', borderRadius: 6, padding: '10px 14px', minHeight: 48, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer', color: t.done ? '#191917' : '#5D645B' }}
                    >
                      <span style={{ fontWeight: 700, fontSize: 17, color: t.done ? '#6B8B75' : '#D0C9B7' }}>{t.done ? '✓' : '○'}</span>
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="grid" style={{ '--cols': 'minmax(0, 1fr) auto', gap: 12, alignItems: 'start' }}>
                  <textarea
                    rows={2}
                    placeholder="Shift note for the family (mood, meals, observations)…"
                    value={drafts[s.id] ?? ''}
                    onChange={(e) => setDrafts({ ...drafts, [s.id]: e.target.value })}
                    style={textareaStyle}
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => doSaveNote(s.id)}
                    style={{ background: 'transparent', color: '#003F3A', border: '1px solid #00564F', borderRadius: 4, padding: '12px 18px', minHeight: 48, fontWeight: 600, fontSize: 15, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer' }}
                  >
                    Save Note
                  </button>
                </div>

                {!!s.note && (
                  <div style={{ background: '#EEE8DA', borderRadius: 6, padding: '12px 16px', fontSize: 15, marginTop: 12, overflowWrap: 'anywhere' }}>
                    <strong>Saved note:</strong> {s.note}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {familyNotes.length > 0 && (
          <div style={{ background: '#FFFFFF', border: '1px solid #D0C9B7', borderRadius: 8, padding: 'clamp(18px, 4vw, 26px)', marginTop: 28 }}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 600, color: '#003F3A', margin: '0 0 10px 0' }}>
              Family notes for your visits
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {familyNotes.map((n) => (
                <div key={n.id} style={{ background: '#F4EDDF', borderRadius: 6, padding: '12px 16px', fontSize: 15, overflowWrap: 'anywhere' }}>
                  <strong style={{ color: '#527569' }}>{n.authorName}:</strong> {n.body}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
